import { Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useClubes } from '../modules/clubes/hooks/useClubes'
import { useAtiradores } from '../modules/atiradores/hooks/useAtiradores'
import { useCampeonatos } from '../modules/campeonatos/hooks/useCampeonatos'
import { useInscricoes } from '../modules/inscricoes/hooks/useInscricoes'
import { useTopResultados } from '../modules/resultados/hooks/useResultados'
import type { Resultado } from '../modules/resultados/types'

interface StatCardProps {
  label: string
  value: number | string
  icon: string
  to: string
  loading?: boolean
}

function StatCard({ label, value, icon, to, loading }: StatCardProps) {
  return (
    <Link
      to={to}
      className="bg-compumaq-pretoSuave rounded-xl p-5 shadow-sm border border-compumaq-dourado/25 hover:shadow-md hover:border-compumaq-dourado/30 transition-all group flex items-center gap-4"
    >
      <div className="text-3xl flex-shrink-0">{icon}</div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-compumaq-textoSuave font-medium uppercase tracking-wide">{label}</p>
        {loading ? (
          <div className="mt-1 h-7 w-16 bg-compumaq-pretoSuave animate-pulse rounded" />
        ) : (
          <p className="text-2xl font-bold text-compumaq-texto group-hover:text-compumaq-doradoClaro transition-colors">
            {value}
          </p>
        )}
      </div>
    </Link>
  )
}

interface ModuleCardProps {
  label: string
  icon: string
  to: string
  desc: string
}

function ModuleCard({ label, icon, to, desc }: ModuleCardProps) {
  return (
    <Link
      to={to}
      className="bg-compumaq-pretoSuave rounded-xl p-5 shadow-sm border border-compumaq-dourado/25 hover:shadow-md hover:border-compumaq-dourado/30 transition-all group"
    >
      <div className="text-3xl mb-3">{icon}</div>
      <h3 className="font-semibold text-compumaq-texto group-hover:text-compumaq-doradoClaro transition-colors">
        {label}
      </h3>
      <p className="text-xs text-compumaq-textoSuave mt-1">{desc}</p>
    </Link>
  )
}

export function DashboardPage() {
  const { usuario } = useAuth()

  const { data: clubesData, isLoading: loadingClubes } = useClubes({ limit: 1 })
  const { data: atiradorData, isLoading: loadingAtiradores } = useAtiradores({ limit: 1 })
  const { data: campeonatoData, isLoading: loadingCampeonatos } = useCampeonatos({ limit: 1 })
  const { data: inscricaoData, isLoading: loadingInscricoes } = useInscricoes({ limit: 1 })
  const { data: topResultados, isLoading: loadingTop } = useTopResultados(20)

  const adminModules: ModuleCardProps[] = [
    { label: 'Campeonatos', icon: '🏆', to: '/campeonatos', desc: 'Gestao de campeonatos' },
    { label: 'Inscricoes', icon: '📋', to: '/inscricoes', desc: 'Gerenciar inscricoes' },
    { label: 'Resultados', icon: '🎯', to: '/resultados', desc: 'Lancamento de resultados' },
    { label: 'Rankings', icon: '🥇', to: '/rankings', desc: 'Consultar rankings' },
    { label: 'Relatorios', icon: '📄', to: '/relatorios', desc: 'Gerar relatorios PDF' },
    { label: 'Acesso', icon: '🔐', to: '/acesso', desc: 'Controle de usuarios e roles' },
  ]

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-compumaq-texto">Dashboard</h2>
        <p className="text-compumaq-textoSuave mt-1">
          Bem-vindo, <strong>{usuario?.nome}</strong>. Selecione um modulo para continuar.
        </p>
      </div>

      {/* Estatísticas reais */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <StatCard
          label="Clubes"
          value={clubesData?.meta?.total ?? 0}
          icon="🏢"
          to="/clubes"
          loading={loadingClubes}
        />
        <StatCard
          label="Atiradores"
          value={atiradorData?.meta?.total ?? 0}
          icon="🎯"
          to="/atiradores"
          loading={loadingAtiradores}
        />
        <StatCard
          label="Campeonatos"
          value={campeonatoData?.meta?.total ?? 0}
          icon="🏆"
          to="/campeonatos"
          loading={loadingCampeonatos}
        />
        <StatCard
          label="Inscricoes"
          value={inscricaoData?.meta?.total ?? 0}
          icon="📋"
          to="/inscricoes"
          loading={loadingInscricoes}
        />
      </div>

      {/* Status operacional */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4 flex items-center gap-3">
        <span className="w-3 h-3 rounded-full bg-green-500 flex-shrink-0 animate-pulse"></span>
        <div>
          <p className="text-sm font-medium text-green-800">Sistema operacional</p>
          <p className="text-xs text-green-600">Todas as funcionalidades disponiveis</p>
        </div>
      </div>

      {/* Botao destaque: Galeria de Fotos e Videos */}
      <Link
        to="/galeria"
        className="block mb-6 rounded-xl bg-gradient-to-r from-compumaq-dourado via-indigo-600 to-purple-600 p-5 text-white shadow-lg hover:shadow-xl transition-shadow group"
      >
        <div className="flex items-center gap-4">
          <div className="text-4xl">📸</div>
          <div className="flex-1">
            <h3 className="text-lg font-bold">Galeria de Fotos e Videos</h3>
            <p className="text-sm text-compumaq-texto/70">Veja os melhores momentos da 1a etapa do campeonato</p>
          </div>
          <div className="text-2xl group-hover:translate-x-1 transition-transform">→</div>
        </div>
      </Link>

      {/* Ranking de Atiradores */}
      <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 shadow-sm overflow-hidden mb-6">
        <div className="bg-gradient-to-r from-compumaq-dourado/10 to-amber-50 border-b border-compumaq-dourado/25 px-4 py-3 flex items-center justify-between">
          <div>
            <span className="font-semibold text-compumaq-texto">Ranking de Atiradores</span>
            <span className="ml-2 text-xs text-compumaq-textoSuave">melhor pontuacao por atirador</span>
          </div>
          <Link to="/resultados" className="text-xs text-compumaq-doradoClaro hover:text-compumaq-doradoClaro font-medium">
            Ver todos
          </Link>
        </div>
        {loadingTop ? (
          <div className="p-6 space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-10 bg-compumaq-pretoSuave animate-pulse rounded" />
            ))}
          </div>
        ) : !topResultados || topResultados.length === 0 ? (
          <div className="text-center py-8 text-compumaq-textoSuave text-sm">
            Nenhum resultado cadastrado ainda
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-compumaq-preto border-b border-compumaq-dourado/25 text-xs">
                <tr>
                  <th className="text-center px-3 py-2 font-semibold text-compumaq-textoSuave w-16">#</th>
                  <th className="text-left px-3 py-2 font-semibold text-compumaq-textoSuave">Atirador</th>
                  <th className="text-left px-3 py-2 font-semibold text-compumaq-textoSuave hidden sm:table-cell">Clube</th>
                  <th className="text-left px-3 py-2 font-semibold text-compumaq-textoSuave hidden md:table-cell">Modalidade</th>
                  <th className="text-right px-3 py-2 font-semibold text-compumaq-textoSuave">Pontuacao</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-compumaq-dourado/25">
                {topResultados.map((r: Resultado, idx: number) => {
                  const isPrecisao = r.modalidade_grupo === 'Precisao'
                  const isIpsc = r.modalidade_grupo === 'IPSC'
                  const pontuacao = isPrecisao ? (r.total_pontos ?? 0) : isIpsc ? Number(r.hit_factor ?? 0) : (r.total ?? 0)
                  const isZero = pontuacao === 0
                  const posicao = idx + 1
                  return (
                    <tr
                      key={r.idresultado}
                      className={`${isZero ? 'opacity-60 bg-compumaq-preto' : idx < 3 ? 'bg-yellow-50/40' : ''} hover:bg-compumaq-dourado/10/40 transition-colors`}
                    >
                      <td className="px-3 py-2 text-center">
                        <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${
                          posicao === 1 ? 'bg-yellow-200 text-yellow-800' :
                          posicao === 2 ? 'bg-compumaq-pretoSuave text-compumaq-texto' :
                          posicao === 3 ? 'bg-orange-200 text-orange-800' :
                          'bg-compumaq-pretoSuave text-compumaq-textoSuave'
                        }`}>
                          {posicao}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <p className={`font-medium text-compumaq-texto truncate ${posicao <= 3 ? 'font-bold' : ''}`}>
                          {r.atirador_nome}
                        </p>
                        <p className="text-xs text-compumaq-textoSuave truncate sm:hidden">
                          {r.clube_nome}
                        </p>
                      </td>
                      <td className="px-3 py-2 text-compumaq-textoSuave hidden sm:table-cell truncate max-w-[140px]">
                        {r.clube_nome}
                      </td>
                      <td className="px-3 py-2 text-compumaq-textoSuave text-xs hidden md:table-cell truncate max-w-[180px]">
                        {r.modalidade_nome}
                        {r.etapa_titulo && <span className="text-compumaq-textoSuave"> · {r.etapa_titulo}</span>}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <p className={`font-bold ${isZero ? 'text-compumaq-textoSuave' : 'text-compumaq-doradoClaro'}`}>
                          {isPrecisao ? `${pontuacao} pts` : isIpsc ? `HF ${pontuacao.toFixed(4)}` : `${pontuacao} acertos`}
                        </p>
                        {isPrecisao && (
                          <p className="text-xs text-compumaq-textoSuave">
                            {r.qtd_x ?? 0}x · {r.qtd_10 ?? 0}·10
                          </p>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Module cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {adminModules.map((card) => (
          <ModuleCard key={card.to} {...card} />
        ))}
      </div>

      {/* User info */}
      <div className="mt-8 bg-compumaq-pretoSuave rounded-xl p-5 shadow-sm border border-compumaq-dourado/25">
        <h3 className="font-semibold text-compumaq-texto mb-3">Seu perfil</h3>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-compumaq-textoSuave">Nome:</span>
            <span className="ml-2 font-medium">{usuario?.nome}</span>
          </div>
          <div>
            <span className="text-compumaq-textoSuave">Email:</span>
            <span className="ml-2 font-medium">{usuario?.email}</span>
          </div>
          <div>
            <span className="text-compumaq-textoSuave">Roles:</span>
            <span className="ml-2 font-medium">{usuario?.roles.join(', ')}</span>
          </div>
        </div>
      </div>
    </div>
  )
}
